/* =============================================================================
   MASKHY — product state
   ONE selected variant for the whole page (MK.variant). The configurator, the purchase
   form, every visible price, the product visuals, the mobile bar and the cart all read
   and write this single state, so the page can never show Black while Shopify has Gray
   selected. Variants, prices and availability come from Shopify (data-product-json).
   ============================================================================= */
(function () {
  'use strict';

  const MK = (window.MK = window.MK || {});
  const theme = window.MaskhyTheme || { strings: {} };
  const strings = theme.strings || {};

  /* ---- Money (mirrors Shopify's money filters) ---------------------------------------- */
  function formatMoney(cents, format, { trimZeros = true } = {}) {
    const value = Number(cents || 0) / 100;
    const delimit = (precision, thousands, decimal) => {
      const [whole, frac] = value.toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${grouped}${decimal}${frac}` : grouped;
    };
    const text = String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
      switch (key) {
        case 'amount': return delimit(2, ',', '.');
        case 'amount_no_decimals': return delimit(0, ',', '.');
        case 'amount_with_comma_separator': return delimit(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return delimit(0, ' ', '');
        case 'amount_with_space_separator': return delimit(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return delimit(2, ' ', '.');
        default: return m;
      }
    });
    return trimZeros ? text.replace(/[.,]00(?!\d)/, '') : text;
  }
  MK.formatMoney = formatMoney;
  // With Shopify Markets the visitor may shop in another currency than the store's; the
  // Liquid money filter knows its format, so theme.liquid passes a sample ("$1,000.00",
  // "€1.000,00") and the format is read back from it.
  function formatFromSample(sample) {
    const m = String(sample || '').match(/1([^\d]?)000([.,])00/);
    if (!m) return null;
    const [whole, thousands, decimal] = m;
    let key = 'amount';
    if (decimal === ',') key = thousands === ' ' ? 'amount_with_space_separator' : 'amount_with_comma_separator';
    else if (thousands === "'") key = 'amount_with_apostrophe_separator';
    else if (thousands === ' ') key = 'amount_with_period_and_space_separator';
    return sample.replace(whole, `{{${key}}}`);
  }
  const moneyFormat = formatFromSample(theme.moneySample) || theme.moneyFormat;
  const money = (cents) => formatMoney(cents, moneyFormat);

  /* ---- Product + the shared variant state ----------------------------------------------- */
  const dataEl = document.querySelector('[data-product-json]');
  const product = dataEl ? JSON.parse(dataEl.textContent) : null;
  if (!product) return;
  MK.product = product;

  const byId = (id) => product.variants.find((v) => v.id === Number(id));
  let current = byId(product.selectedVariantId) || product.variants[0];
  const listeners = new Set();

  MK.variant = {
    get: () => current,
    set(id, source) {
      const v = byId(id);
      if (!v || v === current) return;
      current = v;
      listeners.forEach((fn) => fn(v, source));
    },
    subscribe(fn) {
      listeners.add(fn);
      fn(current, 'init');
      return () => listeners.delete(fn);
    },
  };

  /* ---- Things that simply mirror the state ------------------------------------------------ */
  MK.variant.subscribe((v, source) => {
    document.querySelectorAll('[data-live-price]').forEach((el) => (el.textContent = money(v.price)));
    document.querySelectorAll('[data-option-echo]').forEach((el) => (el.textContent = v.title));
    // product visuals: the same camera, the other real render
    document.querySelectorAll('[data-variant-stage]').forEach((stage) => {
      const next = stage.querySelector(`[data-variant-visual="${v.id}"]`);
      const prev = stage.querySelector('.vvisual.is-current');
      if (!next || next === prev) return;
      const animate = source !== 'init' && document.documentElement.classList.contains('motion-ok');
      stage.querySelectorAll('.vvisual').forEach((el) => el.classList.remove('is-entering', 'is-leaving'));
      if (prev) {
        prev.classList.remove('is-current');
        if (animate) prev.classList.add('is-leaving');
      }
      next.classList.add('is-current');
      if (animate) {
        next.classList.add('is-entering');
        next.addEventListener('animationend', () => next.classList.remove('is-entering'), { once: true });
        if (prev) prev.addEventListener('animationend', () => prev.classList.remove('is-leaving'), { once: true });
      }
    });
    // product pages keep ?variant= in the URL (shareable, and what Shopify expects)
    if (source !== 'init' && window.location.pathname.includes('/products/')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url.toString());
    }
  });

  /* ---- <mk-product-form> — the real Shopify product form ----------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.addButton = this.querySelector('[data-add-button]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.buyNow = this.querySelector('[data-buy-now]');
      this.error = this.querySelector('[data-form-error]');
      this.section = this.closest('[data-purchase]') || document;

      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-value]')) this.onOptionChange();
        if (e.target === this.qtyInput) this.onQuantityChange();
      });
      this.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-step]');
        if (!step) return;
        this.qtyInput.value = Math.min(10, Math.max(1, Number(this.qtyInput.value || 1) + Number(step.dataset.qtyStep)));
        this.onQuantityChange();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.unsubscribe = MK.variant.subscribe((v) => this.render(v));
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    quantity() {
      return Math.min(10, Math.max(1, Number(this.qtyInput ? this.qtyInput.value : 1) || 1));
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('fieldset[data-option-index]')).map((fs) => {
        const checked = fs.querySelector('[data-option-value]:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      const v = product.variants.find((x) => x.options.every((value, i) => options[i] == null || options[i] === value));
      if (v) MK.variant.set(v.id, 'form');
      else this.setUnavailable();
    }

    onQuantityChange() {
      this.qtyInput.value = this.quantity();
      this.updateBuyNow(MK.variant.get());
    }

    render(v) {
      this.idInput.value = v.id;
      // radios follow the shared state (e.g. after choosing in the configurator)
      this.querySelectorAll('fieldset[data-option-index]').forEach((fs) => {
        const i = Number(fs.dataset.optionIndex);
        fs.querySelectorAll('[data-option-value]').forEach((input) => (input.checked = input.value === v.options[i]));
        const label = fs.querySelector('[data-option-selected]');
        if (label) label.textContent = v.options[i] || '';
      });
      const price = this.section.querySelector('[data-price-current]');
      if (price) price.textContent = money(v.price);
      const compareWrap = this.section.querySelector('[data-price-compare-wrap]');
      const compare = this.section.querySelector('[data-price-compare]');
      const showCompare = product.showCompareAtPrice && v.compareAtPrice > v.price;
      if (compareWrap) compareWrap.hidden = !showCompare;
      if (compare) compare.textContent = showCompare ? money(v.compareAtPrice) : '';
      this.addButton.disabled = !v.available;
      this.addLabel.textContent = v.available ? strings.addToCart : strings.soldOut;
      const status = this.section.querySelector('[data-availability]');
      if (status) {
        status.dataset.state = v.available ? 'available' : 'sold-out';
        const label = status.querySelector('[data-availability-label]');
        if (label) label.textContent = v.available ? (v.backorder ? strings.availableToOrder : strings.inStock) : strings.soldOut;
      }
      this.updateBuyNow(v);
      this.syncAvailability();
      this.setError('');
    }

    setUnavailable() {
      this.addButton.disabled = true;
      this.addLabel.textContent = strings.unavailable;
      if (this.buyNow) this.buyNow.classList.add('is-disabled');
    }

    updateBuyNow(v) {
      if (!this.buyNow) return;
      this.buyNow.href = `${theme.routes.cart || '/cart'}/${v.id}:${this.quantity()}`;
      this.buyNow.classList.toggle('is-disabled', !v.available);
      if (v.available) {
        this.buyNow.removeAttribute('aria-disabled');
        this.buyNow.removeAttribute('tabindex');
      } else {
        this.buyNow.setAttribute('aria-disabled', 'true');
        this.buyNow.setAttribute('tabindex', '-1');
      }
    }

    syncAvailability() {
      const options = this.selectedOptions();
      this.querySelectorAll('fieldset[data-option-index]').forEach((fs) => {
        const index = Number(fs.dataset.optionIndex);
        fs.querySelectorAll('[data-option-value]').forEach((input) => {
          const candidate = options.slice();
          candidate[index] = input.value;
          const match = product.variants.find((x) => x.options.every((value, i) => candidate[i] === value));
          const ok = !!(match && match.available);
          const label = input.nextElementSibling;
          if (!label) return;
          label.classList.toggle('is-unavailable', !ok);
          const st = label.querySelector('[data-value-status]');
          if (st) st.textContent = ok ? '' : strings.soldOut;
        });
      });
    }

    setError(message) {
      if (!this.error) return;
      this.error.textContent = message || '';
      this.error.hidden = !message;
    }

    async onSubmit(e) {
      const v = MK.variant.get();
      if (!MK.cart || !v) return; // no JS cart → native POST to /cart/add
      e.preventDefault();
      if (!v.available) return;
      this.setError('');
      this.addButton.classList.add('is-busy');
      this.addButton.setAttribute('aria-busy', 'true');
      this.addLabel.textContent = strings.adding;
      try {
        await MK.cart.add([{ id: v.id, quantity: this.quantity() }], { opener: this.addButton });
        this.addButton.classList.add('is-added');
        setTimeout(() => this.addButton.classList.remove('is-added'), 1800);
      } catch (error) {
        this.setError(error.message);
      } finally {
        this.addButton.classList.remove('is-busy');
        this.addButton.removeAttribute('aria-busy');
        this.addLabel.textContent = MK.variant.get().available ? strings.addToCart : strings.soldOut;
      }
    }
  }
  if (!customElements.get('mk-product-form')) customElements.define('mk-product-form', ProductForm);

  /* ---- Configurator ---------------------------------------------------------------------- */
  document.querySelectorAll('[data-configurator]').forEach((root) => {
    const options = Array.from(root.querySelectorAll('[data-config-variant]'));
    const add = root.querySelector('[data-config-add]');
    const addLabel = root.querySelector('[data-config-add-label]');
    const error = root.querySelector('[data-config-error]');
    options.forEach((btn, i) => {
      btn.addEventListener('click', () => MK.variant.set(btn.dataset.configVariant, 'configurator'));
      btn.addEventListener('keydown', (e) => {
        const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
        if (!dir) return;
        e.preventDefault();
        const next = options[(i + dir + options.length) % options.length];
        next.focus();
        MK.variant.set(next.dataset.configVariant, 'configurator');
      });
    });
    MK.variant.subscribe((v) => {
      options.forEach((btn) => {
        const on = Number(btn.dataset.configVariant) === v.id;
        btn.setAttribute('aria-checked', String(on));
        btn.tabIndex = on ? 0 : -1;
        const variant = byId(btn.dataset.configVariant);
        const st = btn.querySelector('[data-config-status]');
        if (st) st.textContent = variant && variant.available ? '' : strings.soldOut;
      });
      if (add) {
        add.disabled = !v.available;
        addLabel.textContent = v.available ? strings.addToCart : strings.soldOut;
      }
      if (error) error.hidden = true;
    });
    if (add) {
      add.addEventListener('click', async () => {
        const v = MK.variant.get();
        if (!v.available || !MK.cart) return;
        add.classList.add('is-busy');
        addLabel.textContent = strings.adding;
        error.hidden = true;
        try {
          await MK.cart.add([{ id: v.id, quantity: 1 }], { opener: add });
        } catch (err) {
          error.textContent = err.message;
          error.hidden = false;
        } finally {
          add.classList.remove('is-busy');
          addLabel.textContent = MK.variant.get().available ? strings.addToCart : strings.soldOut;
        }
      });
    }
  });

  /* ---- The pair: two masks, each with its own colour, added in one request --------------- */
  document.querySelectorAll('[data-pair]').forEach((root) => {
    const slots = Array.from(root.querySelectorAll('[data-pair-slot]'));
    const add = root.querySelector('[data-pair-add]');
    const addLabel = root.querySelector('[data-pair-add-label]');
    const idle = addLabel ? addLabel.textContent : '';
    const error = root.querySelector('[data-pair-error]');
    const pct = Number(root.dataset.pairPercent || 0);
    const offFor = (v) => Math.round((v.price * pct) / 100); // same rounding as the Liquid
    const chosen = () => slots.map((slot) => byId(slot.dataset.variant)).filter(Boolean);

    const refresh = () => {
      const vs = chosen();
      const full = vs.reduce((sum, v) => sum + v.price, 0);
      const off = vs.reduce((sum, v) => sum + offFor(v), 0);
      const now = root.querySelector('[data-pair-now]');
      const was = root.querySelector('[data-pair-was]');
      const save = root.querySelector('[data-pair-save]');
      if (now) now.textContent = money(full - off);
      if (was) was.textContent = money(full);
      if (save) save.textContent = money(off);
      const ok = vs.length === 2 && vs.every((v) => v.available);
      if (add) add.disabled = !ok;
      if (addLabel) addLabel.textContent = ok ? idle : strings.soldOut;
    };

    const choose = (slot, id, animate) => {
      const v = byId(id);
      if (!v || !v.available || Number(slot.dataset.variant) === v.id) return;
      slot.dataset.variant = v.id;
      slot.querySelectorAll('[data-pair-variant]').forEach((btn) => {
        const on = Number(btn.dataset.pairVariant) === v.id;
        btn.setAttribute('aria-checked', String(on));
        btn.tabIndex = on ? 0 : -1;
      });
      const next = slot.querySelector(`[data-pair-visual="${v.id}"]`);
      const prev = slot.querySelector('.vvisual.is-current');
      const motionOk = animate && document.documentElement.classList.contains('motion-ok');
      slot.querySelectorAll('.vvisual').forEach((el) => el.classList.remove('is-entering', 'is-leaving'));
      if (prev) {
        prev.classList.remove('is-current');
        if (motionOk) prev.classList.add('is-leaving');
      }
      if (next) {
        next.classList.add('is-current');
        if (motionOk) next.classList.add('is-entering');
      }
      if (error) error.hidden = true;
      refresh();
    };

    slots.forEach((slot) => {
      const buttons = Array.from(slot.querySelectorAll('[data-pair-variant]'));
      buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => choose(slot, btn.dataset.pairVariant, true));
        btn.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
          if (!dir) return;
          e.preventDefault();
          const next = buttons[(i + dir + buttons.length) % buttons.length];
          next.focus();
          choose(slot, next.dataset.pairVariant, true);
        });
      });
    });

    if (add) {
      add.addEventListener('click', async () => {
        if (!MK.cart) return;
        const items = [];
        chosen().forEach((v) => {
          const line = items.find((it) => it.id === v.id);
          if (line) line.quantity += 1;
          else items.push({ id: v.id, quantity: 1 });
        });
        add.classList.add('is-busy');
        if (addLabel) addLabel.textContent = strings.adding;
        error.hidden = true;
        try {
          await MK.cart.add(items, { opener: add });
        } catch (err) {
          error.textContent = err.message;
          error.hidden = false;
        } finally {
          add.classList.remove('is-busy');
          refresh();
        }
      });
    }
    refresh();
  });

  /* ---- Mobile purchase bar: only after the construction scene, never over the shop ---------- */
  const bar = document.querySelector('[data-buybar]');
  if (bar && 'IntersectionObserver' in window) {
    const after = document.querySelector('[data-scene="exploded"]') || document.querySelector('[data-scene="reveal"]');
    // …nor over the last scene, which has its own price and Shop button
    const hideOver = Array.from(document.querySelectorAll('[data-configurator], [data-pair], [data-purchase], [data-scene="final"], .footer'));
    const inView = new Set();
    let passed = false;
    const update = () => {
      const show = passed && inView.size === 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      const link = bar.querySelector('[data-buybar-link]');
      if (link) link.tabIndex = show ? 0 : -1;
    };
    if (after) {
      const check = () => {
        const r = after.getBoundingClientRect();
        const next = r.bottom < window.innerHeight * 0.5; // the product has come apart, clicked back together, and moved on
        if (next !== passed) {
          passed = next;
          update();
        }
      };
      window.addEventListener('scroll', check, { passive: true });
      check();
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? inView.add(e.target) : inView.delete(e.target)));
      update();
    });
    hideOver.forEach((el) => io.observe(el));
  }
})();
